import { createSelector } from 'reselect';
import isEmpty from 'lodash/isEmpty';
import { getFormValues, isInvalid } from 'redux-form';
import { initialState } from './reducer';
import { ICEPACK_FORM_NAME, ICEPACK_TABLE_ROWS_NAMES } from './constants';
import { thousandSeparator } from '../reqs-filter/utils';
import { getLocalStorageItem } from '../../utils/localStorage';

export const selectIcepackModalDomain = state => state.icepackModalReducer || initialState;
export const selectRouterDomain = state => state.router || {};
export const selectProfileModalDomain = state => state.profileModalReducer || {};

export const getIcepackInfoSelector = () =>
	createSelector(selectIcepackModalDomain, ({ icepackInfo }) => {
		if (!icepackInfo) return [];

		return Object.entries(ICEPACK_TABLE_ROWS_NAMES).reduce((result, [key, value]) => {
			if (key === 'partner') {
				result.push({
					id: key,
					title: value,
					value: icepackInfo.partner.name,
				});

				return result;
			}

			if (key === 'sizes') {
				result.push({
					id: key,
					title: value,
					value: {
						length: thousandSeparator(icepackInfo.length),
						width: thousandSeparator(icepackInfo.width),
						height: thousandSeparator(icepackInfo.height),
					},
				});

				return result;
			}

			if (key === 'weight') {
				result.push({
					id: key,
					title: value,
					value: `${thousandSeparator(icepackInfo.weight)} кг`,
				});

				return result;
			}

			if (key === 'volume') {
				result.push({
					id: key,
					title: value,
					value: `${thousandSeparator(icepackInfo.volume)} л`,
				});

				return result;
			}

			if (key === 'package_quantity') {
				result.push({
					id: key,
					title: value,
					value: `${thousandSeparator(icepackInfo[key])} шт`,
				});

				return result;
			}

			if (key === 'pallet_quantity') {
				if (!icepackInfo[key]) {
					return result;
				}

				result.push({
					id: key,
					title: value,
					value: `${thousandSeparator(icepackInfo[key])} шт`,
				});

				return result;
			}

			if (key === 'author') {
				result.push({
					id: key,
					title: value,
					value: {
						name: `${icepackInfo?.user?.last_name || ''} ${icepackInfo?.user?.first_name || ''}`,
						role: icepackInfo?.user?.role?.name || '',
					},
				});

				return result;
			}

			result.push({
				id: key,
				title: value,
				value: icepackInfo[key] ?? '-',
			});

			return result;
		}, []);
	});

export const getIsIcepackInfoLoadingSelector = () =>
	createSelector(selectIcepackModalDomain, ({ isIcepackInfoLoading }) => isIcepackInfoLoading);

export const getIsIcepackCreatingSelector = () =>
	createSelector(selectIcepackModalDomain, ({ isIcepackCreating }) => isIcepackCreating);

export const getIsIcepackUpdatingSelector = () =>
	createSelector(selectIcepackModalDomain, ({ isIcepackUpdating }) => isIcepackUpdating);

export const getIsIcepackDeletingSelector = () =>
	createSelector(selectIcepackModalDomain, ({ isIcepackDeleting }) => isIcepackDeleting);

export const getFormInitialValuesSelector = () =>
	createSelector(selectIcepackModalDomain, selectProfileModalDomain, ({ icepackInfo }, { userInfo }) => {
		if (!icepackInfo) {
			const companyId = getLocalStorageItem('user_data')?.companyId || userInfo?.company?.id;

			return {
				partner_id: companyId,
			};
		}

		return {
			...icepackInfo,
			partner_id: icepackInfo.partner?.id,
		};
	});

export const getFormIsInvalidSelector = () => createSelector(isInvalid(ICEPACK_FORM_NAME), isInvalid => isInvalid);

export const getDataForSaveSelector = () => createSelector(getFormValues(ICEPACK_FORM_NAME), formValues => formValues);

export const getIcepackNameSelector = () =>
	createSelector(selectIcepackModalDomain, ({ icepackInfo }) => icepackInfo?.name ?? '');

export const getIsModalShownSelector = () =>
	createSelector(selectRouterDomain, ({ location: { query } }) => {
		return Boolean(!isEmpty(query) && query?.modal === 'icepack');
	});

export const getIsIcepackAddFormOpenSelector = () =>
	createSelector(selectRouterDomain, ({ location: { query } }) => {
		return Boolean(!isEmpty(query) && query?.modal === 'icepack' && query?.mode === 'add');
	});

export const getIsIcepackEditFormOpenSelector = () =>
	createSelector(selectRouterDomain, ({ location: { query } }) => {
		return Boolean(!isEmpty(query) && query?.modal === 'icepack' && query?.mode === 'edit');
	});

export const getActiveIcepackIdSelector = () =>
	createSelector(selectRouterDomain, ({ location: { query } }) => {
		return query?.modal === 'icepack' ? query?.id : null;
	});

export const getIsAuditTabShownSelector = () =>
	createSelector(selectRouterDomain, ({ location: { query } }) => {
		return Boolean(!isEmpty(query) && query?.modal === 'icepack' && query?.id && query?.tab === 'audit');
	});

export const getPartnersOptionsSelector = () =>
	createSelector(selectIcepackModalDomain, ({ partners }) =>
		partners.map(partner => ({
			key: partner.id,
			value: partner.id,
			text: partner.name,
		})),
	);

export const getIsPartnersLoadingSelector = () =>
	createSelector(selectIcepackModalDomain, ({ isPartnersLoading }) => isPartnersLoading);
